import * as Sentry from '@sentry/vue'
import OpenAIUtils from '@/utils/open-ai/OpenAIUtils'
import openAIAPI from '@/api/openai'
import Utils from '@/util'
import i18nPresenter from '@/i18nPresenter'

const state = {
  autoCompleteSlideOption: [],
  loadingAutocomplete: false,
  autocompleteError: null,
}

const getters = {
  autoCompleteSlideOption: (state) => state.autoCompleteSlideOption,
  isApprovalAllowed: (state) => state.autoCompleteSlideOption.length > 0,
  isLoadingAutocomplete: (state) => state.loadingAutocomplete,
  autocompleteError: (state) => state.autocompleteError,
}
const services = {
  async fetchSlideOptions(questionContent) {
    try {
      const response = await openAIAPI.getSlideOptionAutocomplete({ question: questionContent })
      return response.quiz_options
    } catch (error) {
      throw new Error(i18nPresenter.t('Error fetching answers from API'))
    }
  },

  filterValidOptions(activeSlideOptions) {
    return activeSlideOptions.filter((option) => option && option.title && option.title.length > 0)
  },

  sortOptionsByCorrectness(quizOptions) {
    return quizOptions.sort((a, b) => b.correct - a.correct)
  },

  calculateAvailableSlots(filteredOptions, MAX_ANSWERS) {
    return MAX_ANSWERS - filteredOptions.length
  },

  // Function to handle adding new quiz options
  async addOptions(dispatch, prioritizedQuizOptions) {
    await Promise.all(prioritizedQuizOptions.map(async (quizOption, order) => {
      await dispatch('slideOptionModule/addOptionAction', {
        title: quizOption.content,
        correct: quizOption.correct,
        order: 0.1 * (prioritizedQuizOptions.length - order),
      }, { root: true })
    }))
  },
}

const mutations = {

  setAutoCompleteSlideOption(state, suggestions) {
    state.autoCompleteSlideOption = suggestions
  },
  setLoadingAutocomplete(state, isLoading) {
    state.loadingAutocomplete = isLoading
  },
  setAutocompleteError(state, error) {
    state.autocompleteError = error
  },
}

const actions = {
  async getSlideOptionAutocompleteAction({ commit, rootState, dispatch }, questionContent) {
    try {
      const { SlideOptions: activeSlideOptions } = rootState.slideActive
      const MAX_ANSWERS = 8 // Set max number of allowed answers

      // Validation check
      const isQuestionContentValid = OpenAIUtils.shouldGenerateSlideOptions(questionContent)
      if (!isQuestionContentValid) {
        return
      }

      commit('setLoadingAutocomplete', true)

      let fetchedQuizOptions
      try {
        fetchedQuizOptions = await services.fetchSlideOptions(questionContent)
      } catch (apiError) {
        commit('setAutocompleteError', i18nPresenter.t('Error fetching answers from API'))
        commit('setLoadingAutocomplete', false)
        return
      }

      await Promise.all(activeSlideOptions.map((option) => {
        if (!option || !option.title || option.title.length === 0) {
          return dispatch('slideOptionModule/deleteOptionAction', option, { root: true })
        }
        return option
      }))

      const filteredOptions = services.filterValidOptions(activeSlideOptions)

      // Check if there are too many valid options
      if (filteredOptions.length >= MAX_ANSWERS) {
        commit('setAutocompleteError', i18nPresenter.t('There are already too many options. Please remove some to make room for new ones.'))
        commit('setLoadingAutocomplete', false)
        return
      }

      const optionsToAdd = services.calculateAvailableSlots(filteredOptions, MAX_ANSWERS)
      const availableQuizOptions = fetchedQuizOptions.slice(0, optionsToAdd)
      if (availableQuizOptions.length === 0) {
        commit('setAutocompleteError', i18nPresenter.t('No new answers found.'))
        commit('setLoadingAutocomplete', false)
        return
      }
      const prioritizedQuizOptions = services.sortOptionsByCorrectness(availableQuizOptions)

      await services.addOptions(dispatch, prioritizedQuizOptions)

      commit('setAutoCompleteSlideOption', prioritizedQuizOptions)
      commit('setLoadingAutocomplete', false)
      commit('setAutocompleteError', null)
      Utils.showToastSuccess(`${availableQuizOptions.length} answers have been generated.`)
    } catch (e) {
      Sentry.captureException(e)
      commit('setLoadingAutocomplete', false)
    }
  },

  setAutoCompleteSlideOptionAction({ commit }, suggestions) {
    commit('setAutoCompleteSlideOption', suggestions)
  },
}
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
